'use client'

import { useState } from 'react'

type Status = 'idle' | 'loading' | 'success' | 'error'

/**
 * End-of-page capture. The old index ended with no next step at all, making
 * every hard-won visitor a one-time visitor. Posts the email to
 * /api/blog/subscribe, which stores it in Supabase and pings Telegram.
 */
export function SubscribeCta() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setStatus('loading')
    setMessage('')

    try {
      const res = await fetch('/api/blog/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()

      if (!res.ok) {
        setStatus('error')
        setMessage(data.error || 'Something went wrong. Please try again.')
        return
      }

      setStatus('success')
      setMessage(
        data.alreadySubscribed
          ? "You're already on the list — nothing more to do."
          : "You're in. I'll be in touch when there's something worth sending."
      )
      setEmail('')
    } catch {
      setStatus('error')
      setMessage('Network error. Please try again.')
    }
  }

  return (
    <section
      id="subscribe"
      className="scroll-mt-24 my-10 md:my-12 rounded-xl bg-primary text-primary-foreground p-8 md:p-10 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 md:gap-12 items-center"
    >
      <div>
        <div className="font-display text-2xl font-bold mb-2.5">
          Get the next one in your inbox
        </div>
        <p className="text-[15px] leading-relaxed text-primary-foreground/70 text-pretty">
          I&rsquo;ll email you when something new goes up — no schedule, no spam, just
          the post. Unsubscribe anytime.
        </p>
      </div>

      <div>
        {status === 'success' ? (
          <p
            className="rounded-lg bg-accent/15 border border-accent/40 px-4 py-3 text-[15px] text-primary-foreground"
            role="status"
          >
            {message}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2.5">
            <label htmlFor="subscribe-email" className="sr-only">
              Email address
            </label>
            <input
              id="subscribe-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              disabled={status === 'loading'}
              // sm:flex-1, not flex-1: the form stacks to flex-col on mobile,
              // where flex-1 applies flex-basis:0 to the *height* and collapsed
              // this field to a 20px sliver despite the h-12.
              className="h-12 sm:flex-1 px-4 rounded-lg bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 outline-none focus:ring-2 focus:ring-accent text-base disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="inline-flex shrink-0 items-center justify-center h-12 px-6 rounded-lg bg-accent text-accent-foreground text-[15px] font-bold hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        )}
        {status === 'error' && (
          <p className="mt-2 text-[13px] text-primary-foreground/80" role="alert">
            {message}
          </p>
        )}
      </div>
    </section>
  )
}
